import Fuse from "fuse.js";
import { AnimatePresence, motion } from "motion/react";
import React, { useMemo } from "react";
import { Input } from "../../ui-library/Input/Input.tsx";
import { LoadingDots } from "../../ui-library/LoadingDots/LoadingDots.tsx";
import { HistoryItemMenu } from "./HistoryItemMenu.tsx";
import { type HistorySidebar } from "./HistorySidebar.tsx";
import styles from "./HistorySidebarRedesign.module.css";
import { truncateOnWord } from "./truncateOnWord.ts";
import { NewChatIcon } from "../../ui-library/_icons/NewChatIcon.tsx";

export const HistorySidebarRedesign: typeof HistorySidebar = (props) => {
  const [searchValue, setSearchValue] = React.useState("");
  const fuse = useMemo(
    () =>
      new Fuse(props.items, {
        keys: ["label", "data.brief", "data.questions"],
      }),
    [props.items],
  );
  const items = useMemo(() => {
    if (searchValue) {
      const result = fuse.search(searchValue);
      return result.map((item) => item.item);
    }
    return props.items;
  }, [fuse, props.items, searchValue]);
  return (
    <nav className={styles.HistoryContainer}>
      <button className={styles.NewChatButton} onClick={props.onNewChat}>
        {props.newChatLabel || (
          <>
            <NewChatIcon className={styles.Icon} /> New Workflow
          </>
        )}
      </button>
      <hr className={styles.HorizontalSeparator} />
      <h3 className={styles.HistoryTitle}>
        History{" "}
        {props.loading && (
          <>
            (loading
            <LoadingDots />)
          </>
        )}
      </h3>
      <div className={styles.SearchContainer}>
        <Input
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          placeholder="Search chats"
          iconAfter={<SearchIcon />}
        />
      </div>
      <ul className={styles.HistoryList}>
        <AnimatePresence>
          {items.map(({ label, value }) => (
            <motion.li
              exit={{
                opacity: 0,
                height: 0,
                margin: 0,
                padding: 0,
                overflow: "hidden",
              }}
              className={
                styles.HistoryItem +
                " " +
                (props.activeItem === value ? styles.active : "")
              }
              key={value}
              onClick={() => props.onSelectionChange(value)}
            >
              {label && truncateOnWord(label, 90)}
              {props.ContextMenuComponent ? (
                <props.ContextMenuComponent id={value} />
              ) : (
                <HistoryItemMenu
                  onClickDelete={() => {
                    props.onDelete?.(value);
                  }}
                  onClickRename={() => {
                    props.onRename?.(value);
                  }}
                />
              )}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      <div>
        {props.enableTermsAndConditions && (
          <p className={styles.TermsAndConditions}>
            <a href="/terms-and-conditions" target="_blank">
              Terms and Conditions
            </a>
          </p>
        )}
      </div>
    </nav>
  );
};

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 18.9995L15.325 15.3996C14.825 15.8329 14.242 16.1704 13.576 16.4121C12.91 16.6538 12.2013 16.7746 11.45 16.7746C9.64733 16.7746 8.12167 16.1496 6.873 14.8996C5.62433 13.6496 5 12.1413 5 10.3746C5 8.60794 5.625 7.09961 6.875 5.84961C8.125 4.59961 9.6375 3.97461 11.4125 3.97461C13.1875 3.97461 14.6958 4.59961 15.9375 5.84961C17.1792 7.09961 17.8 8.60919 17.8 10.3784C17.8 11.0925 17.6833 11.7829 17.45 12.4496C17.2167 13.1163 16.8667 13.7413 16.4 14.3246L20.1 17.8995L19 18.9995ZM11.425 15.2746C12.7792 15.2746 13.9303 14.7954 14.8783 13.8371C15.8261 12.8788 16.3 11.7246 16.3 10.3746C16.3 9.02461 15.8261 7.87044 14.8783 6.91211C13.9303 5.95378 12.7792 5.47461 11.425 5.47461C10.057 5.47461 8.89417 5.95378 7.9365 6.91211C6.97883 7.87044 6.5 9.02461 6.5 10.3746C6.5 11.7246 6.97883 12.8788 7.9365 13.8371C8.89417 14.7954 10.057 15.2746 11.425 15.2746Z"
      fill="#CFCFCF"
      fillOpacity="0.86"
    />
  </svg>
);
